import { useQuery } from '@tanstack/react-query'
import {
  MealReviewChip,
  MealReviewChips,
  useMealReviewChips,
} from '@tovala/browser-apis-combinedapi'
import { Button } from '@tovala/component-library'
import { clsx } from 'clsx'
import { useEffect, useMemo, useRef, useState } from 'react'

import { events } from 'analytics/events'
import { track } from 'utils/analytics'
import { DATE_FORMATS, formatDate, isDateSame, parseToDate } from 'utils/dates'

import StarSharpIcon from 'components/common/icons/StarSharpIcon'
import { reviewQueries } from 'components/core/queries/reviews'
import { MealRating } from './hooks'

const COMMENT_CHARACTER_LIMIT = 120
const TOTAL_STARS = 5

const MealReviewCard = ({
  mealID,
  rating,
  shouldScrollIntoView = false,
}: {
  mealID: number
  rating: MealRating
  shouldScrollIntoView?: boolean
}) => {
  const [isCommentExpanded, setIsCommentExpanded] = useState(false)

  const cardRef = useRef<HTMLDivElement>(null)

  const { data: mealReviewChips, isPending: isPendingMealReviewChips } =
    useMealReviewChips()
  const { data: ratingChips, isPending: isPendingRatingChips } = useQuery(
    reviewQueries.ratingChips(),
  )
  const isPendingChips = isPendingMealReviewChips || isPendingRatingChips

  const shownChips = useMemo(() => {
    const chipNamesByID = mealReviewChips
      ? getChipNamesByID(mealReviewChips)
      : new Map<string, string>()

    ratingChips?.chips.forEach(({ id, name }) => {
      chipNamesByID.set(id, name)
    })

    return rating.chips.flatMap((id) => {
      const name = chipNamesByID.get(id)

      return name ? [{ id, name }] : []
    })
  }, [mealReviewChips, rating.chips, ratingChips])

  const created = parseToDate(rating.created)
  const formattedCreated = formatDate(created, {
    // Per design, the year is only displayed once it's not the current one.
    format: isDateSame(created, new Date(), { units: 'years' })
      ? DATE_FORMATS.MONTH_FULL_DAY
      : DATE_FORMATS.MONTH_FULL_DAY_YEAR_FULL,
  })

  const isCommentTruncated = rating.comment.length > COMMENT_CHARACTER_LIMIT
  const comment =
    isCommentTruncated && !isCommentExpanded
      ? `${rating.comment.slice(0, COMMENT_CHARACTER_LIMIT).trimEnd()}…`
      : rating.comment

  const chipIDsShown = shownChips.map(({ id }) => id).join(',')

  const eventProps = useMemo(() => {
    return {
      chips_id: chipIDsShown || null,
      meal_id: mealID,
      rating: rating.stars,
      review_shown: rating.comment ? 'yes' : null,
    }
  }, [chipIDsShown, mealID, rating.comment, rating.stars])

  // Wait for the chips to resolve before reporting the review as shown, otherwise
  // chips_id would look empty for a review that does have chips.
  const isWaitingForChips = rating.chips.length > 0 && isPendingChips

  useEffect(() => {
    if (isWaitingForChips) {
      return
    }

    track(events.MEAL_DETAILS_RATING_SHOWN, eventProps)
  }, [eventProps, isWaitingForChips])

  useEffect(() => {
    if (!shouldScrollIntoView || isWaitingForChips) {
      return
    }

    cardRef.current?.scrollIntoView({ block: 'center' })
  }, [isWaitingForChips, shouldScrollIntoView])

  return (
    <div
      ref={cardRef}
      className="mb-8 space-y-3 rounded-lg border border-grey-3 bg-white p-4 md:mb-6"
    >
      <h3 className="text-k/14_120 font-semibold">Your review</h3>

      <div className="flex items-center space-x-2">
        <div aria-hidden="true" className="flex items-center space-x-1">
          {Array.from({ length: TOTAL_STARS }, (_, index) => {
            return (
              <span
                key={index}
                className={clsx(
                  'block h-[14px] w-[14px]',
                  index < rating.stars ? 'text-orange-1' : 'text-[#D9D9D9]',
                )}
              >
                <StarSharpIcon />
              </span>
            )
          })}
        </div>

        <span className="sr-only">
          {rating.stars} out of {TOTAL_STARS} stars
        </span>

        <span className="text-k/14_120 text-grey-9">{formattedCreated}</span>
      </div>

      {shownChips.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {shownChips.map(({ id, name }) => {
            return (
              <li
                key={id}
                className="rounded-full bg-grey-2 px-3 py-[6px] text-k/12_120"
              >
                {name}
              </li>
            )
          })}
        </ul>
      )}

      {rating.comment && (
        <div className="space-y-2">
          <p className="text-k/16_125">{comment}</p>

          {isCommentTruncated && (
            <Button
              buttonStyle="link"
              onClick={() => {
                setIsCommentExpanded(!isCommentExpanded)

                track(
                  isCommentExpanded
                    ? events.MEAL_DETAILS_RATING_COLLAPSE
                    : events.MEAL_DETAILS_RATING_EXPAND,
                  eventProps,
                )
              }}
              size="auto"
            >
              <span className="text-k/14_120 text-orange-2">
                {isCommentExpanded ? 'See less' : 'See more'}
              </span>
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default MealReviewCard

/**
 * Flattens the chip groups into a name lookup. Chips are returned in three groups
 * that can each nest a level deep, and a review only tells us the IDs it used.
 */
export function getChipNamesByID(chips: MealReviewChips) {
  const chipNamesByID = new Map<string, string>()

  const addChips = (chipsToAdd: MealReviewChip[]) => {
    chipsToAdd.forEach(({ children, id, name }) => {
      chipNamesByID.set(id, name)

      if (children) {
        addChips(children)
      }
    })
  }

  addChips([...chips.feedback, ...chips.negative, ...chips.positive])

  return chipNamesByID
}
